/* eslint-disable no-console */
import React, { ComponentType, ReactElement } from 'react';
import LazyLoad from 'react-lazyload';
import styled, { css } from 'styled-components';

import { Loader as DefaultLoader } from 'shared/components/Loader';
import { getImageSrcs } from 'shared/utils/getImageSrcs';

// ImageV2 is different from Image in that ImageV2 uses the new image service 'go-image-v2'
// go-image-v2 API Docs:
// https://github.com/goatapp/go-image-v2/blob/master/README.md#http-api-documentation
export interface IImageProps {
  alpha?: boolean;
  alt: string;
  background?: string; // RGB value to use for transparent PNGs, ex: background=255,255,255
  className?: string;
  crop?: boolean;
  disableLazyLoad?: boolean;
  getImageHost?: (host?: string) => string;
  height?: number;
  loader?: ComponentType;
  missingAssetPath?: string;
  noSrcModification?: boolean;
  onClick?: () => void;
  onError?: () => void;
  onLoad?: () => void;
  resizeable?: boolean;
  size?: number; // takes the longest edge of the image and uses that as the resizing value
  sizes: string;
  src: string;
  type?: string; // overrides content negotiation based on Accepts header, options: jpeg, png, webp
  width?: number;
  quality?: number; // JPEG and WEBP image quality between 1-100 and divisible by 5. Defaults to 85
}

export const BaseImage = (props: IImageProps): ReactElement => {
  const {
    alpha,
    alt,
    background,
    className,
    crop = false,
    disableLazyLoad,
    getImageHost,
    height,
    loader: Loader = DefaultLoader,
    missingAssetPath,
    noSrcModification,
    onClick,
    onError,
    onLoad,
    quality,
    resizeable = true,
    size,
    sizes,
    src = 'missing.png',
    type,
    width,
  } = props;

  if (!sizes) {
    console.error('No "sizes" prop detected on <Image /> component.');
  }

  /** Flag that determines if no asset image is present. */
  const isMissing = /missing.png/.test(src || '');

  const imageSrcs = getImageSrcs({
    alpha,
    background,
    crop,
    getImageHost,
    height,
    missingAssetPath,
    noSrcModification,
    quality,
    resizeable,
    size,
    src,
    type,
    width,
  });

  const ImageLoader = Loader ? (
    <BaseImage.LoaderWrapper>
      <Loader />
    </BaseImage.LoaderWrapper>
  ) : null;

  const baseImage = (
    <BaseImage.StyledImage
      {...props}
      alt={alt}
      className={className}
      onClick={onClick}
      onError={onError}
      onLoad={onLoad}
      sizes={sizes}
      {...imageSrcs}
      missingAssetPath={missingAssetPath}
      isMissing={isMissing}
    />
  );

  if (disableLazyLoad) {
    return baseImage;
  }

  return (
    <LazyLoad once placeholder={ImageLoader}>
      {baseImage}
    </LazyLoad>
  );
};

// images use objectFit contain
// to support responsive / client sizes
// that differ from the delivered image
BaseImage.StyledImage = styled.img<{
  missingAssetPath?: string;
  isMissing: boolean;
}>`
  width: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  // Sets a centered missing asset as background if product image is missing
  ${({ missingAssetPath, isMissing }) => {
    if (missingAssetPath && isMissing) {
      return css`
        background: url(${missingAssetPath}) no-repeat center;
      `;
    }
  }}
`;
BaseImage.LoaderWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;
